import { clsx } from "clsx";
import { Shield, ShieldCheck, ShieldAlert } from "lucide-react";
import type { ServiceTier } from "@/types";
import { SERVICE_TIERS } from "@/types";

interface ServiceTierSelectorProps {
  value: ServiceTier;
  onChange: (tier: ServiceTier) => void;
}

const tierIcons = {
  A: ShieldAlert,
  B: ShieldCheck,
  C: Shield,
};

const tierBorderColors = {
  A: "border-amber-500 bg-amber-500/5",
  B: "border-blue-500 bg-blue-500/5",
  C: "border-gray-500 bg-gray-500/5",
};

const tierBadgeColors = {
  A: "bg-amber-500/20 text-amber-400",
  B: "bg-blue-500/20 text-blue-400",
  C: "bg-gray-500/20 text-gray-400",
};

export function ServiceTierSelector({
  value,
  onChange,
}: ServiceTierSelectorProps) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      {SERVICE_TIERS.map((tierInfo) => {
        const Icon = tierIcons[tierInfo.tier];
        const isSelected = value === tierInfo.tier;

        return (
          <button
            key={tierInfo.tier}
            type="button"
            onClick={() => onChange(tierInfo.tier)}
            className={clsx(
              "relative rounded-xl border-2 p-5 text-left transition-all",
              isSelected
                ? tierBorderColors[tierInfo.tier]
                : "border-gray-800 bg-gray-900/50 hover:border-gray-700"
            )}
          >
            {isSelected && (
              <div className="absolute right-3 top-3">
                <div className="h-3 w-3 rounded-full bg-blue-500" />
              </div>
            )}

            <div className="flex items-center gap-3">
              <Icon
                className={clsx(
                  "h-6 w-6",
                  isSelected ? "text-blue-400" : "text-gray-500"
                )}
              />
              <div>
                <span
                  className={clsx(
                    "rounded-full px-2 py-0.5 text-xs font-bold",
                    tierBadgeColors[tierInfo.tier]
                  )}
                >
                  Tier {tierInfo.tier}
                </span>
                <h3 className="mt-1 text-base font-semibold text-gray-200">
                  {tierInfo.name}
                </h3>
              </div>
            </div>

            <p className="mt-3 text-sm text-gray-400">
              {tierInfo.description}
            </p>

            <ul className="mt-4 space-y-2 text-xs text-gray-400">
              <li className="flex items-center gap-2">
                <span className="h-1 w-1 rounded-full bg-gray-500" />
                {tierInfo.ha_level}
              </li>
              <li className="flex items-center gap-2">
                <span className="h-1 w-1 rounded-full bg-gray-500" />
                {tierInfo.replication}
              </li>
              <li className="flex items-center gap-2">
                <span className="h-1 w-1 rounded-full bg-gray-500" />
                {tierInfo.backup_retention_days}-day backup retention
              </li>
              <li className="flex items-center gap-2">
                <span className="h-1 w-1 rounded-full bg-gray-500" />
                SLA: {tierInfo.sla}
              </li>
            </ul>
          </button>
        );
      })}
    </div>
  );
}
